import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import Logo from "@/components/Logo";
import { Button } from "@/components/kit";
import { ease } from "@/lib/motion";

const LOGIN_URL = "https://app.meiraai.com/login"; // TODO: replace with platform login URL when provided

const NAV = [
  { label: "Platform", to: "/platform" },
  { label: "AI Security", to: "/ai-security" },
  { label: "Governance", to: "/governance" },
  { label: "Assurance", to: "/assurance" },
  { label: "Why Meira", to: "/why-meira" },
  { label: "About", to: "/about" },
];

/* The sheet is its own component so its entry/exit animation is driven by
   AnimatePresence rather than by mounting and unmounting a bare div. */
function MobileSheet({ open, onClose, pathname }) {
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="mobile-menu"
          id="mobile-menu"
          data-testid="mobile-menu"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0, transition: { duration: 0.2, ease } }}
          transition={{ duration: 0.25, ease }}
          /* Covers the viewport edge to edge — dvh so mobile browser chrome
             cannot leave a strip of page showing under the sheet. */
          className="fixed inset-0 z-40 h-[100dvh] w-full bg-ink lg:hidden"
        >
          <div className="tech-grid pointer-events-none absolute inset-0 opacity-40" />
          <div className="pointer-events-none absolute -top-32 left-1/2 h-[380px] w-[560px] -translate-x-1/2 rounded-full bg-brand/[0.06] blur-[120px]" />

          {/* the bar itself is drawn by the header above this sheet, so the
              content starts below it */}
          <div className="relative flex h-full flex-col overflow-y-auto overscroll-contain px-6 pb-10 pt-[76px]">
            <motion.nav
              initial="hidden"
              animate="show"
              variants={{ hidden: {}, show: { transition: { staggerChildren: 0.045, delayChildren: 0.08 } } }}
              className="flex flex-col"
              aria-label="Main"
            >
              {NAV.map((n, i) => {
                const activeLink = pathname === n.to;
                return (
                  <motion.div
                    key={n.to}
                    variants={{ hidden: { opacity: 0, y: 14 }, show: { opacity: 1, y: 0, transition: { duration: 0.45, ease } } }}
                  >
                    <Link
                      to={n.to}
                      onClick={onClose}
                      data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                      aria-current={activeLink ? "page" : undefined}
                      className="flex items-baseline gap-4 border-b border-line-soft py-[18px]"
                    >
                      <span className="font-mono text-[10px] tracking-[0.2em] text-content-faint">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span
                        className={`font-display text-[26px] font-medium leading-none tracking-tight ${
                          activeLink ? "text-brand" : "text-content"
                        }`}
                      >
                        {n.label}
                      </span>
                    </Link>
                  </motion.div>
                );
              })}
            </motion.nav>

            <motion.div
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45, delay: 0.36, ease }}
              className="mt-8"
            >
              <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-content-faint">Trust &amp; access</div>
              <div className="mt-3 flex flex-col">
                <Link
                  to="/verify"
                  onClick={onClose}
                  data-testid="mobile-nav-verify"
                  className="flex items-center justify-between border-b border-line-soft py-4 text-[15px] font-medium text-brand"
                >
                  Verify Certificate
                  <span aria-hidden>→</span>
                </Link>
                <a
                  href={LOGIN_URL}
                  data-testid="mobile-nav-login"
                  className="flex items-center justify-between border-b border-line-soft py-4 text-[15px] font-medium text-content-muted"
                >
                  Login
                  <span aria-hidden>→</span>
                </a>
              </div>
            </motion.div>

            {/* CTA sits at the foot of the sheet, thumb-reachable */}
            <motion.div
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45, delay: 0.44, ease }}
              className="mt-auto pt-8"
            >
              <Button
                to="/contact"
                variant="primary"
                onClick={onClose}
                data-testid="mobile-nav-book-demo"
                className="w-full py-4 uppercase tracking-[0.12em]"
              >
                Book a Demo
              </Button>
              <p className="mt-4 text-center font-mono text-[10px] uppercase tracking-[0.2em] text-content-faint">
                Test · Govern · Prove
              </p>
            </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default function Navbar({ onScrollLock }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  // While the sheet is up the page behind it must not scroll — otherwise a
  // touch drag moves the page under a full-screen overlay.
  useEffect(() => {
    if (!open) return;
    const { body, documentElement: root } = document;
    const prevOverflow = body.style.overflow;
    const prevRootOverflow = root.style.overflow;
    const prevPadding = body.style.paddingRight;
    const gutter = window.innerWidth - root.clientWidth;
    // Both, not just body: the stylesheet gives <html> an overflow-x, which
    // stops `overflow` on <body> from propagating to the viewport.
    body.style.overflow = "hidden";
    root.style.overflow = "hidden";
    if (gutter > 0) body.style.paddingRight = `${gutter}px`;
    onScrollLock?.(true);
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      body.style.overflow = prevOverflow;
      root.style.overflow = prevRootOverflow;
      body.style.paddingRight = prevPadding;
      window.removeEventListener("keydown", onKey);
      onScrollLock?.(false);
    };
  }, [open, onScrollLock]);

  // The bar needs a ground of its own whenever it is not sitting on the page
  // top: scrolled, or holding the open sheet.
  const solid = scrolled || open;

  return (
    <>
      <header
        className={`fixed top-0 z-50 w-full transition-colors duration-500 ${
          solid ? "border-b border-line bg-ink/90 backdrop-blur-xl" : "border-b border-transparent"
        }`}
      >
        <nav className="mx-auto flex h-[68px] max-w-[1240px] items-center justify-between gap-3 px-5 sm:h-[72px] md:px-6 lg:gap-4 xl:gap-6 xl:px-10">
          <Link to="/" data-testid="nav-logo" aria-label="Meira AI home" className="min-w-0 shrink-0" onClick={() => setOpen(false)}>
            {/* the link carries the accessible name, so the logo is decorative */}
            <Logo title={null} className="block h-[18px] w-auto sm:h-5" />
          </Link>

          <div className="hidden items-center gap-3 lg:flex xl:gap-7">
            {NAV.map((n) => {
              const activeLink = location.pathname === n.to;
              return (
                <Link
                  key={n.to}
                  to={n.to}
                  data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                  className={`whitespace-nowrap text-[13px] font-medium transition-colors xl:text-sm ${activeLink ? "text-content" : "text-content-muted hover:text-content"}`}
                >
                  {n.label}
                </Link>
              );
            })}
          </div>

          <div className="hidden shrink-0 items-center gap-2 lg:flex xl:gap-3">
            <Link
              to="/verify"
              data-testid="nav-verify"
              className="whitespace-nowrap text-[13px] font-medium text-brand transition-colors hover:text-brand-soft xl:text-sm"
            >
              Verify Certificate
            </Link>
            <a
              href={LOGIN_URL}
              data-testid="nav-login"
              className="whitespace-nowrap text-[13px] font-medium text-content-muted transition-colors hover:text-content xl:text-sm"
            >
              Login
            </a>
            <Button to="/contact" variant="primary" data-testid="nav-book-demo" className="whitespace-nowrap px-3.5 text-xs uppercase tracking-[0.08em] xl:px-6 xl:tracking-[0.12em]">
              Book a Demo
            </Button>
          </div>

          <button
            type="button"
            className="-mr-2 flex h-11 w-11 shrink-0 items-center justify-center text-content lg:hidden"
            onClick={() => setOpen((v) => !v)}
            data-testid="nav-mobile-toggle"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            aria-controls="mobile-menu"
          >
            {open ? <X size={24} strokeWidth={1.6} /> : <Menu size={24} strokeWidth={1.6} />}
          </button>
        </nav>
      </header>

      <MobileSheet open={open} onClose={() => setOpen(false)} pathname={location.pathname} />
    </>
  );
}
